// AI-CONTEXT: See docs/database.md (ProjectMarks)
// Sterren en labels op een project in het portfolio-overzicht. Ze horen bij de
// KIJKER, niet bij het project: Deccos-beheer en elk portfolio markeren hun
// eigen projecten, en geen van beide ziet wat de ander markeerde (keuze Gijs
// 2026-10-16). Daarom niet op het CompagnyMeta-document van het project — dat
// is gedeeld én openbaar leesbaar — maar in een eigen collectie `ProjectMarks`
// met de kijker als `CompagnyID`.
//
// De betekenis van de ster en de labelvorm zijn die van huishoudens en
// deelnemers: `Starred` strikt `=== true` (isStarred), `Labels` een lijst
// sleutels uit `CompagnyMeta.ProjectLabels` van de kijker (labelsOf).

import { isStarred } from '../starred/starred'
import { hasLabel, labelsOf } from '../adressHelpers/resolveAdressLabels'

/**
 * @typedef {object} ProjectMark
 * @property {string} ProjectID - CompagnyID van het gemarkeerde project
 * @property {string} CompagnyID - De kijker die markeerde
 * @property {boolean} [Starred]
 * @property {Array<string>} [Labels]
 */

/**
 * Eén document per kijker en project, met een vaste id: een dubbele klik of
 * twee tabbladen maken zo nooit een tweede document voor hetzelfde project.
 * @param {string} viewerId - CompagnyID van de kijker
 * @param {string} projectId - CompagnyID van het project
 * @returns {string}
 */
const projectMarkDocId = (viewerId, projectId) => `${viewerId}_${projectId}`

/**
 * De velden die élke schrijfactie meestuurt. Met `set(..., { merge: true })`
 * maakt de eerste schrijfactie het document aan; de regel vraagt dan om de
 * kijker als `CompagnyID`.
 * @param {string} viewerId
 * @param {string} projectId
 * @param {*} timestamp - `timestamp` uit firebase/config
 * @returns {{ID: string, CompagnyID: string, Compagny: string, ProjectID: string, Timestamp: *}}
 */
const projectMarkBase = (viewerId, projectId, timestamp) => ({
    ID: projectMarkDocId(viewerId, projectId),
    CompagnyID: viewerId,
    Compagny: viewerId,
    ProjectID: projectId,
    Timestamp: timestamp,
})

/**
 * @param {Array<ProjectMark>} [marks]
 * @returns {Map<string, ProjectMark>} Per ProjectID
 */
const indexProjectMarks = (marks) => {
    const map = new Map()
    for (const mark of Array.isArray(marks) ? marks : []) {
        if (mark?.ProjectID) map.set(mark.ProjectID, mark)
    }
    return map
}

/**
 * @param {Array<{CompagnyID: string}>} projects
 * @param {Map<string, ProjectMark>} marksByProject
 * @param {{starOnly?: boolean, labelKey?: string}} filters - '' of ontbrekend = geen labelfilter
 * @returns {Array<object>} In de aangeleverde volgorde
 */
const filterProjectsByMarks = (projects, marksByProject, { starOnly = false, labelKey = '' } = {}) => {
    if (!starOnly && !labelKey) return projects
    return projects.filter((p) => {
        const mark = marksByProject.get(p.CompagnyID)
        if (starOnly && !isStarred(mark)) return false
        if (labelKey && !hasLabel(mark, labelKey)) return false
        return true
    })
}

/**
 * De projecten die de kijker een ster gaf, ongeacht hun status: de ster is
 * een bewuste keuze, en een gestard project dat op inactief springt is juist
 * iets om te zien.
 * @param {Array<{CompagnyID: string}>} projects
 * @param {Map<string, ProjectMark>} marksByProject
 * @returns {Array<object>}
 */
const starredProjects = (projects, marksByProject) =>
    filterProjectsByMarks(projects, marksByProject, { starOnly: true })

/**
 * De labels van de organisatie als keuzemenu-opties en als opzoektabel per
 * sleutel. Eén afleiding voor het projectenoverzicht en de kaart 'Projecten
 * met ster'.
 * @param {Array<{key: string, label: string, color: string}>} [labels] - Uit resolveProjectLabels
 * @returns {{options: Array<{id: string, label: string}>, byKey: Object<string, {key: string, label: string, color: string}>}}
 */
const projectLabelLookup = (labels) => {
    const lijst = Array.isArray(labels) ? labels : []
    return {
        options: lijst.map((l) => ({ id: l.key, label: l.label })),
        byKey: Object.fromEntries(lijst.map((l) => [l.key, l])),
    }
}

/**
 * De labels van één project die de organisatie nog toont. Een verborgen of
 * weggehaald label blijft op het project staan, maar hoort niet in beeld.
 * @param {ProjectMark} [mark]
 * @param {Object<string, object>} byKey - Uit projectLabelLookup
 * @returns {Array<string>} In de volgorde van het project
 */
const visibleLabelKeys = (mark, byKey) => labelsOf(mark).filter((key) => byKey[key])

export { projectMarkDocId, projectMarkBase, indexProjectMarks, filterProjectsByMarks, starredProjects, projectLabelLookup, visibleLabelKeys }
